import { useTranslation } from 'react-i18next';
import { PlusIcon } from '@heroicons/react/24/outline';
import { twJoin } from 'tailwind-merge';

import MainButton from '@/components/ui/custom/buttons/MainButton';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import Popup from '@/components/ui/custom/Popup';
import useToggle from '@/data/hook/useToggle';
import { Section } from '@/models/Book';
import { scrollBarYClassName } from '@/utils/scrollBarClassName';
import SectionTitle from '../sections/view/SectionTitle';
import ConditionalWrapper from '@/components/ui/custom/ConditionalWrapper';
import { Tooltip } from '@/components/ui/custom/dataDisplay/Tooltip';

type SectionsPopupProps = {
  sections: Section[];
  readOnly?: boolean;
  loading?: boolean;
  handleAdd(sectionId: number): void;
};

export default function SectionsPopup(props: SectionsPopupProps) {
  const [t] = useTranslation('translation', {
    keyPrefix: 'common',
  });

  const { readOnly, sections, loading, handleAdd } = props;

  const { isOpen, toggle, close, open } = useToggle();

  return (
    <ConditionalRenderer condition={!readOnly}>
      <Popup
        opened={isOpen}
        onOpen={open}
        onClose={close}
        onToggle={toggle}
        disabled={loading}
        className={{
          reference: 'pb-3.5 px-3.5',
          popup: loading ? 'disabled' : '',
        }}
        reference={
          <ConditionalWrapper
            condition={!sections.length}
            wrapper={<Tooltip text={t('allSectionsAdd')} />}
          >
            <MainButton
              icon={<PlusIcon />}
              text={t('add')}
              color="custom"
              className="text-primary font-600"
              disabled={!sections.length}
            />
          </ConditionalWrapper>
        }
      >
        <ul
          className={twJoin(
            'rounded-xl shadow-default bg-base-100 border border-neutral-content flex flex-col min-w-32 p-3.5 gap-2.5 max-w-[12.5rem] max-h-[12.5rem]',
            scrollBarYClassName,
          )}
        >
          {sections.map(section => (
            <button
              data-testid={`sectionButton${section.id}`}
              onClick={() => {
                handleAdd(section.id);
                toggle();
              }}
              key={section.id}
            >
              <SectionTitle
                icon={section.icon}
                title={section.title}
                className={{ base: 'w-full' }}
              />
            </button>
          ))}
        </ul>
      </Popup>
    </ConditionalRenderer>
  );
}
